import { useCallback, useEffect, useMemo, useState } from "react";
import { T, base } from "../lib/theme";
import { apiFetch } from "../lib/api";
import { Btn, Card, Icon, Toggle } from "../components";

const DEFAULT_SETTINGS = {
  enabled: true,
  mode: "draft",
  tone: "Helpful, concise, professional",
  signature: "",
  ignoreRules: "Ignore newsletters, promotions, spam, no-reply senders, receipts, and automated notifications.",
};

const FILTERS = [
  ["all", "All"],
  ["drafted", "Needs review"],
  ["ignored", "Ignored"],
  ["sent", "Sent"],
];

const STATUS_META = {
  new:       ["New", T.sky],
  processing:["Processing", T.sky],
  drafted:   ["Drafted", T.amber],
  suggested: ["Suggested", T.violet],
  sent:      ["Sent", T.online],
  ignored:   ["Ignored", T.text3],
  failed:    ["Failed", T.rose],
  sending:   ["Sending", T.sky],
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 9,
  border: `1px solid ${T.border1}`,
  background: T.bg0,
  color: T.text1,
  fontSize: 13,
  outline: "none",
  fontFamily: base.font,
};

function Label({ children }) {
  return <div style={{ fontSize: 11, color: T.text3, marginBottom: 7, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>{children}</div>;
}

function Pill({ children, color = T.indigo }) {
  return <span style={{ fontSize: 11, fontWeight: 800, color, background: `${color}16`, border: `1px solid ${color}32`, borderRadius: 999, padding: "4px 9px", whiteSpace: "nowrap" }}>{children}</span>;
}

function MessageCard({ message, active, onSelectDraft, onGenerate, busy }) {
  const [label, color] = STATUS_META[message.status] || [message.status || "Unknown", T.text3];
  const ignored = message.status === "ignored";
  const reason = message.reason || (ignored ? "Matched your ignore rules." : "");

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "1fr auto",
      gap: 14,
      padding: 14,
      background: active ? T.indigoDim : T.bg0,
      border: `1px solid ${active ? T.indigo : T.border1}`,
      borderRadius: 10,
      alignItems: "start",
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: T.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {message.subject || "(no subject)"}
          </div>
        </div>
        <div style={{ fontSize: 12, color: T.text3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{message.from}</div>
        <div style={{ fontSize: 12, color: T.text2, lineHeight: 1.55, marginTop: 8 }}>{message.snippet || message.bodyText?.slice(0, 180)}</div>
        {reason && (
          <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 8, background: ignored ? T.amberDim : T.bg2, color: ignored ? T.amber : T.text3, fontSize: 12, lineHeight: 1.45, border: `1px solid ${ignored ? `${T.amber}30` : T.border1}` }}>
            {ignored ? "Ignored because: " : "Note: "}{reason}
          </div>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
        <Pill color={color}>{label}</Pill>
        {(message.status === "ignored" || message.status === "new") && (
          <button onClick={() => onGenerate(message)} disabled={busy} style={{
            padding: "7px 10px",
            borderRadius: 8,
            border: `1px solid ${T.indigo}38`,
            background: T.indigoDim,
            color: T.indigo,
            cursor: busy ? "not-allowed" : "pointer",
            fontSize: 12,
            fontWeight: 800,
            whiteSpace: "nowrap",
          }}>
            Generate reply
          </button>
        )}
        {message.status === "drafted" && (
          <button onClick={() => onSelectDraft(message)} style={{
            padding: "7px 10px",
            borderRadius: 8,
            border: `1px solid ${T.border1}`,
            background: T.bg2,
            color: T.text2,
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 800,
            whiteSpace: "nowrap",
          }}>
            Review draft
          </button>
        )}
      </div>
    </div>
  );
}

export default function EmailPage({ token, showToast }) {
  const [account, setAccount] = useState(null);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [stats, setStats] = useState({ messageCount: 0, draftCount: 0 });
  const [knowledge, setKnowledge] = useState("");
  const [messages, setMessages] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [status, msgData, draftData, knowledgeData] = await Promise.all([
      apiFetch("/api/email/status", {}, token),
      apiFetch("/api/email/messages", {}, token),
      apiFetch("/api/email/drafts", {}, token),
      apiFetch("/api/knowledge", {}, token),
    ]);
    if (status?.settings) setSettings({ ...DEFAULT_SETTINGS, ...status.settings });
    setAccount(status?.account || null);
    setStats(status?.stats || { messageCount: 0, draftCount: 0 });
    setKnowledge(knowledgeData?.knowledge || "");
    setMessages(msgData?.messages || []);
    setDrafts(draftData?.drafts || []);
    setSelectedDraft(prev => {
      const list = draftData?.drafts || [];
      if (!prev) return list.find(d => d.status === "draft") || null;
      return list.find(d => d._id === prev._id) || list.find(d => d.status === "draft") || null;
    });
  }, [token]);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  const connected = account?.connected;
  const filteredMessages = useMemo(() => {
    if (filter === "all") return messages;
    if (filter === "drafted") return messages.filter(m => m.status === "drafted" || m.status === "suggested");
    return messages.filter(m => m.status === filter);
  }, [messages, filter]);

  const counts = useMemo(() => ({
    all: messages.length,
    drafted: messages.filter(m => m.status === "drafted" || m.status === "suggested").length,
    ignored: messages.filter(m => m.status === "ignored").length,
    sent: messages.filter(m => m.status === "sent").length,
  }), [messages]);

  const connect = async () => {
    setBusy(true);
    try {
      const d = await apiFetch("/api/email/connect", { method: "POST" }, token);
      if (d?.authUrl) window.location.href = d.authUrl;
      else showToast?.(d?.error || "Gmail connection is not configured", "error");
    } finally {
      setBusy(false);
    }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const d = await apiFetch("/api/email/sync", { method: "POST" }, token);
      if (d?.error) showToast?.(d.error, "error");
      else showToast?.(`Email synced: ${d.synced || 0} new`);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      const d = await apiFetch("/api/email/settings", { method: "POST", body: JSON.stringify(settings) }, token);
      if (d?.error) showToast?.(d.error, "error");
      else showToast?.("Email automation saved");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await apiFetch("/api/email/disconnect", { method: "POST" }, token);
      showToast?.("Gmail disconnected");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const generateReply = async (message) => {
    setBusy(true);
    try {
      const d = await apiFetch(`/api/email/messages/${message._id}/generate`, { method: "POST" }, token);
      if (d?.error) showToast?.(d.error, "error");
      else showToast?.("Reply draft generated");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const selectDraftForMessage = (message) => {
    const draft = drafts.find(d => String(d.messageId) === String(message._id) && d.status === "draft");
    if (draft) setSelectedDraft(draft);
  };

  const sendDraft = async () => {
    if (!selectedDraft) return;
    setBusy(true);
    try {
      const d = await apiFetch(`/api/email/drafts/${selectedDraft._id}/send`, {
        method: "POST",
        body: JSON.stringify({ subject: selectedDraft.subject, body: selectedDraft.body }),
      }, token);
      if (d?.error) showToast?.(d.error, "error");
      else showToast?.("Draft sent");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = async () => {
    if (!selectedDraft) return;
    setBusy(true);
    try {
      const d = await apiFetch(`/api/email/drafts/${selectedDraft._id}`, {
        method: "POST",
        body: JSON.stringify({ subject: selectedDraft.subject, body: selectedDraft.body }),
      }, token);
      if (d?.error) showToast?.(d.error, "error");
      else showToast?.("Draft updated");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const discardDraft = async () => {
    if (!selectedDraft) return;
    setBusy(true);
    try {
      await apiFetch(`/api/email/drafts/${selectedDraft._id}/discard`, { method: "POST" }, token);
      showToast?.("Draft discarded");
      setSelectedDraft(null);
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (loading) return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 26, height: 26, border: `2px solid ${T.border2}`, borderTopColor: T.indigo, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
    </div>
  );

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "26px 28px 40px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", marginBottom: 22 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: T.text1 }}>Email Assistant</div>
          <div style={{ fontSize: 13, color: T.text3, marginTop: 3 }}>Standalone Gmail automation with explainable draft and ignore decisions.</div>
        </div>
        <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
          {connected && <Btn variant="outline" onClick={sync} disabled={busy}><Icon name="activity" size={13} /> Sync now</Btn>}
          {connected ? <Btn variant="danger" onClick={disconnect} disabled={busy}>Disconnect</Btn> : <Btn variant="primary" onClick={connect} disabled={busy}><Icon name="mail" size={13} color="#fff" /> Connect Gmail</Btn>}
        </div>
      </div>

      <div className="grid-3" style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr", gap: 14, marginBottom: 18 }}>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, color: T.text3, marginBottom: 4 }}>Mailbox</div>
              <div style={{ fontSize: 15, color: T.text1, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{connected ? account.email || "Gmail connected" : "Not connected"}</div>
              <div style={{ fontSize: 12, color: T.text3, marginTop: 4 }}>{account?.lastSyncAt ? `Last sync ${new Date(account.lastSyncAt).toLocaleString()}` : "Connect Gmail to begin"}</div>
            </div>
            <Pill color={connected ? T.online : T.rose}>{connected ? "Connected" : "Offline"}</Pill>
          </div>
        </Card>
        <Card>
          <div style={{ fontSize: 12, color: T.text3 }}>Emails processed</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: T.text1, marginTop: 8 }}>{stats.messageCount || 0}</div>
        </Card>
        <Card>
          <div style={{ fontSize: 12, color: T.text3 }}>Drafts waiting</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: T.text1, marginTop: 8 }}>{stats.draftCount || 0}</div>
        </Card>
      </div>

      <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "360px 1fr", gap: 18 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: T.text1 }}>Automation</div>
                <div style={{ fontSize: 12, color: T.text3, marginTop: 3 }}>Default is draft-first. Auto-send must be selected.</div>
              </div>
              <Toggle value={settings.enabled} onChange={v => setSettings(s => ({ ...s, enabled: v }))} />
            </div>

            <Label>Reply mode</Label>
            <div style={{ display: "grid", gap: 8, marginBottom: 16 }}>
              {[
                ["draft", "Create Gmail draft"],
                ["suggest", "Suggest inside dashboard"],
                ["auto_send", "Auto-send reply"],
              ].map(([mode, label]) => (
                <button key={mode} onClick={() => setSettings(s => ({ ...s, mode }))} style={{
                  padding: "10px 12px",
                  borderRadius: 9,
                  border: `1px solid ${settings.mode === mode ? T.indigo : T.border1}`,
                  background: settings.mode === mode ? T.indigoDim : T.bg0,
                  color: settings.mode === mode ? T.indigo : T.text2,
                  textAlign: "left",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 800,
                }}>{label}</button>
              ))}
            </div>

            <Label>Tone</Label>
            <input value={settings.tone} onChange={e => setSettings(s => ({ ...s, tone: e.target.value }))} style={{ ...inputStyle, marginBottom: 14 }} />
            <Label>Ignore rules</Label>
            <textarea value={settings.ignoreRules} onChange={e => setSettings(s => ({ ...s, ignoreRules: e.target.value }))} style={{ ...inputStyle, minHeight: 96, resize: "vertical", lineHeight: 1.5, marginBottom: 10 }} />
            <div style={{ fontSize: 12, color: T.text3, lineHeight: 1.5, marginBottom: 14 }}>
              Emails are ignored only when they match automated/newsletter patterns or your AI ignore rules. Human-looking short emails should now receive a draft.
            </div>
            <Btn variant="primary" onClick={saveSettings} disabled={busy}><Icon name="upload" size={13} color="#fff" /> Save</Btn>
          </Card>

          <Card>
            <div style={{ fontSize: 14, fontWeight: 800, color: T.text1, marginBottom: 14 }}>Knowledge</div>
            <div style={{ padding: 12, borderRadius: 9, background: T.bg0, border: `1px solid ${T.border1}`, marginBottom: 14 }}>
              <div style={{ fontSize: 13, color: T.text1, fontWeight: 800 }}>{knowledge.length.toLocaleString()} shared knowledge characters</div>
              <div style={{ fontSize: 12, color: T.text3, lineHeight: 1.5, marginTop: 5 }}>
                Email replies use the same Knowledge Base as the WhatsApp assistant.
              </div>
            </div>
            <Label>Signature</Label>
            <textarea value={settings.signature} onChange={e => setSettings(s => ({ ...s, signature: e.target.value }))} placeholder="Regards,\nYour Team" style={{ ...inputStyle, minHeight: 76, resize: "vertical", lineHeight: 1.55 }} />
          </Card>
        </div>

        <div style={{ display: "grid", gap: 18, minWidth: 0 }}>
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: T.text1 }}>Inbox Monitor</div>
                <div style={{ fontSize: 12, color: T.text3, marginTop: 2 }}>Every email shows what the assistant did and why.</div>
              </div>
              <Pill>{filteredMessages.length} shown</Pill>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
              {FILTERS.map(([id, label]) => (
                <button key={id} onClick={() => setFilter(id)} style={{
                  padding: "7px 11px",
                  borderRadius: 999,
                  border: `1px solid ${filter === id ? T.indigo : T.border1}`,
                  background: filter === id ? T.indigoDim : T.bg0,
                  color: filter === id ? T.indigo : T.text2,
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 800,
                }}>{label} {counts[id] ? `(${counts[id]})` : ""}</button>
              ))}
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              {filteredMessages.length === 0 ? (
                <div style={{ padding: 24, textAlign: "center", color: T.text3, background: T.bg0, borderRadius: 10, border: `1px solid ${T.border1}` }}>
                  {connected ? "No emails in this view. Sync after a new unread email arrives." : "Connect Gmail to monitor incoming mail."}
                </div>
              ) : filteredMessages.map(m => (
                <MessageCard
                  key={m._id}
                  message={m}
                  busy={busy}
                  active={selectedDraft && String(selectedDraft.messageId) === String(m._id)}
                  onGenerate={generateReply}
                  onSelectDraft={selectDraftForMessage}
                />
              ))}
            </div>
          </Card>

          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: T.text1 }}>Draft Queue</div>
                <div style={{ fontSize: 12, color: T.text3, marginTop: 2 }}>Review AI replies before sending.</div>
              </div>
              {selectedDraft && <Pill color={selectedDraft.status === "sent" ? T.online : T.amber}>{selectedDraft.status}</Pill>}
            </div>
            <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 14 }}>
              <div style={{ display: "grid", gap: 8, alignContent: "start", maxHeight: 380, overflowY: "auto" }}>
                {drafts.length === 0 ? (
                  <div style={{ padding: 18, color: T.text3, background: T.bg0, borderRadius: 10, border: `1px solid ${T.border1}` }}>No drafts yet.</div>
                ) : drafts.map(d => (
                  <button key={d._id} onClick={() => setSelectedDraft(d)} style={{
                    padding: 11,
                    borderRadius: 10,
                    border: `1px solid ${selectedDraft?._id === d._id ? T.indigo : T.border1}`,
                    background: selectedDraft?._id === d._id ? T.indigoDim : T.bg0,
                    color: T.text1,
                    cursor: "pointer",
                    textAlign: "left",
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.subject}</div>
                    <div style={{ fontSize: 11, color: T.text3, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.to}</div>
                  </button>
                ))}
              </div>

              {selectedDraft ? (
                <div>
                  <Label>Subject</Label>
                  <input value={selectedDraft.subject} onChange={e => setSelectedDraft(d => ({ ...d, subject: e.target.value }))} style={{ ...inputStyle, marginBottom: 12 }} />
                  <Label>Reply</Label>
                  <textarea value={selectedDraft.body} onChange={e => setSelectedDraft(d => ({ ...d, body: e.target.value }))} style={{ ...inputStyle, minHeight: 220, resize: "vertical", lineHeight: 1.6, marginBottom: 12 }} />
                  <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
                    <Btn variant="secondary" onClick={saveDraft} disabled={busy || selectedDraft.status !== "draft"}>Save draft</Btn>
                    <Btn variant="primary" onClick={sendDraft} disabled={busy || selectedDraft.status !== "draft"}><Icon name="send" size={13} color="#fff" /> Send</Btn>
                    <Btn variant="outline" onClick={discardDraft} disabled={busy || selectedDraft.status !== "draft"}>Discard</Btn>
                  </div>
                </div>
              ) : (
                <div style={{ minHeight: 260, display: "flex", alignItems: "center", justifyContent: "center", color: T.text3, background: T.bg0, border: `1px solid ${T.border1}`, borderRadius: 10 }}>
                  Select a draft to review.
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
